import { faCircleCheck, faExclamationCircle } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Box, Typography } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'

import { isValidToken } from './token-highlighting'

const useStyles = makeStyles((theme) => ({
  container: {
    marginTop: theme.spacing(0.5),
  },
  validIcon: {
    color: theme.palette.success.main,
    marginRight: theme.spacing(0.5),
  },
  validText: {
    color: theme.palette.success.main,
  },
  invalidIcon: {
    color: theme.palette.error.main,
    marginRight: theme.spacing(0.5),
  },
  invalidText: {
    color: theme.palette.error.main,
  },
}))

/**
 * TokenValidationFeedback - Visual feedback component for token validation
 *
 * Displays validation status for tokens:
 * - Green checkmark if all tokens are valid
 * - Red error icon if any tokens are invalid (with list of invalid tokens)
 * - Nothing if there are no tokens (neutral state)
 */
const TokenValidationFeedback = ({ tokens }: { tokens: string[] }) => {
  const classes = useStyles()

  // If no tokens found, show nothing (neutral state)
  if (!tokens || tokens.length === 0) {
    return null
  }

  // Separate valid and invalid tokens
  const invalidTokens = tokens.filter((token) => !isValidToken(token))
  const allValid = invalidTokens.length === 0

  if (allValid) {
    // Valid state: green checkmark
    return (
      <Box
        display="flex"
        alignItems="center"
        className={classes.container}
      >
        <FontAwesomeIcon
          icon={faCircleCheck}
          className={classes.validIcon}
        />
        <Typography
          variant="body2"
          className={classes.validText}
        >
          All subject tokens valid
        </Typography>
      </Box>
    )
  }

  // Invalid state: red error icon with list of invalid tokens
  // Remove duplicates for cleaner display
  const uniqueInvalidTokens = [...new Set(invalidTokens)]

  return (
    <Box
      display="flex"
      alignItems="center"
      className={classes.container}
    >
      <FontAwesomeIcon
        icon={faExclamationCircle}
        className={classes.invalidIcon}
      />
      <Typography
        variant="body2"
        className={classes.invalidText}
      >
        Invalid subject token{uniqueInvalidTokens.length > 1 ? 's' : ''}:{' '}
        {uniqueInvalidTokens.map((t) => `{{${t}}}`).join(', ')}
      </Typography>
    </Box>
  )
}

export default TokenValidationFeedback
